import React, { useEffect, useState, useMemo } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { API_BASE } from '../config';

/**
 * Embedded Stripe checkout page.
 *
 * Two modes (chosen by `kind` prop):
 *  - 'credit_pack'  → calls /create-payment-intent (one-time)
 *  - 'subscription' → calls /create-subscription-intent (recurring)
 *
 * On success, the webhook is what updates the user's role/credits.
 * The frontend just confirms the PaymentIntent and shows a success screen.
 */
function CheckoutPage({ kind, pack, tier, period, onBack, onSuccess }) {
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState('');
  const [meta, setMeta] = useState(null);             // { amount_cents, currency, credits/tier/period }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const token = typeof window !== 'undefined' ? localStorage.getItem('astrogem_token') : null;

  // Bootstrap: load publishable key then create the intent
  useEffect(() => {
    let cancelled = false;
    async function bootstrap() {
      try {
        if (!token) throw new Error('Sign in to continue');

        // 1. Load publishable key (cached after first call)
        const pkRes = await fetch(`${API_BASE}/api/v3/billing/publishable-key`).then(r => r.json());
        if (!pkRes.publishable_key) throw new Error('Stripe is not configured on the server. Please contact support.');
        if (cancelled) return;
        setStripePromise(loadStripe(pkRes.publishable_key));

        // 2. Create the intent (credit pack OR subscription)
        const endpoint = kind === 'subscription'
          ? '/api/v3/billing/create-subscription-intent'
          : '/api/v3/billing/create-payment-intent';
        const body = kind === 'subscription' ? { tier, period } : { pack };

        const intentRes = await fetch(`${API_BASE}${endpoint}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
          body: JSON.stringify(body),
        }).then(r => r.json());

        if (cancelled) return;
        if (!intentRes.success) throw new Error(intentRes.error || 'Could not start checkout');

        setClientSecret(intentRes.client_secret);
        setMeta(intentRes);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    bootstrap();
    return () => { cancelled = true; };
  }, [kind, pack, tier, period, token]);

  const options = useMemo(() => clientSecret ? {
    clientSecret,
    appearance: {
      theme: 'night',
      variables: {
        colorPrimary: '#a855f7',
        colorBackground: '#1e293b',
        colorText: '#ffffff',
        colorDanger: '#ef4444',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        borderRadius: '8px',
      },
      rules: {
        '.Input': { backgroundColor: '#0f172a', border: '1px solid #334155' },
        '.Input:focus': { borderColor: '#a855f7' },
      },
    },
    loader: 'auto',
  } : null, [clientSecret]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-950 to-slate-900 text-white">
      <nav className="border-b border-slate-800 bg-slate-900/50 backdrop-blur sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={onBack} className="text-white/60 hover:text-white text-sm">← Back to Dashboard</button>
          <span className="text-sm text-white/40">🔒 Secured by Stripe</span>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-4 py-12">
        <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8">
          <CheckoutSummary kind={kind} pack={pack} tier={tier} period={period} meta={meta} />

          {loading && (
            <div className="text-center py-10 text-white/60">
              <div className="text-2xl mb-2">⏳</div>
              Preparing secure checkout…
            </div>
          )}

          {error && (
            <div className="bg-red-500/10 border border-red-500/40 rounded-lg p-4 text-sm text-red-300 mt-4">
              ⚠️ {error}
            </div>
          )}

          {clientSecret && stripePromise && (
            <Elements stripe={stripePromise} options={options}>
              <CheckoutForm
                clientSecret={clientSecret}
                onSuccess={onSuccess}
                kind={kind}
                meta={meta}
              />
            </Elements>
          )}

          <IndiaNotice />
        </div>

        <p className="text-center text-xs text-white/40 mt-6">
          Your card details are entered directly into Stripe's secure iframe — they never touch AstroGem's servers.
          We comply with PCI-DSS via Stripe (SAQ A).
        </p>
      </main>
    </div>
  );
}

function CheckoutSummary({ kind, pack, tier, period, meta }) {
  let title, sub, amount;
  if (kind === 'subscription') {
    title = `${(tier || '').charAt(0).toUpperCase() + (tier || '').slice(1)} Plan — ${period === 'yearly' ? 'Yearly' : 'Monthly'}`;
    sub = period === 'yearly' ? 'Billed once per year. Cancel anytime.' : 'Billed monthly. Cancel anytime.';
  } else {
    const labels = { pack_500: '500', pack_2k: '2,000', pack_10k: '10,000', pack_50k: '50,000' };
    title = `🪙 ${labels[pack] || pack} AI Credits`;
    sub = 'One-time purchase. Credits never expire. Used after monthly allowance is exhausted.';
  }
  if (meta?.amount_cents != null) {
    amount = `$${(meta.amount_cents / 100).toFixed(2)} ${(meta.currency || 'USD').toUpperCase()}`;
  }
  return (
    <div className="border-b border-slate-700 pb-4 mb-6">
      <h1 className="text-2xl font-bold">{title}</h1>
      <p className="text-white/60 text-sm mt-1">{sub}</p>
      {amount && <p className="text-3xl font-bold mt-3 text-purple-300">{amount}</p>}
    </div>
  );
}

function CheckoutForm({ clientSecret, onSuccess, kind, meta }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setErr('');

    const { error: stripeErr, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
      confirmParams: {
        return_url: window.location.origin + '/?paid=1',
      },
    });

    setSubmitting(false);
    if (stripeErr) {
      setErr(stripeErr.message || 'Payment failed');
      return;
    }
    if (paymentIntent?.status === 'succeeded') {
      onSuccess?.({ kind, meta, paymentIntent });
    } else if (paymentIntent?.status === 'processing') {
      onSuccess?.({ kind, meta, paymentIntent, processing: true });
    } else if (paymentIntent?.status === 'requires_action') {
      setErr('Additional authentication required by your bank. Please complete the verification.');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <PaymentElement options={{ layout: 'tabs' }} />
      {err && (
        <div className="bg-red-500/10 border border-red-500/40 rounded-lg p-3 text-sm text-red-300">
          ⚠️ {err}
        </div>
      )}
      <button
        type="submit"
        disabled={!stripe || submitting}
        className="w-full px-6 py-4 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 disabled:opacity-50 rounded-lg font-bold transition text-lg"
      >
        {submitting ? '⏳ Processing…' : kind === 'subscription' ? 'Start Subscription' : 'Buy Credits'}
      </button>
    </form>
  );
}

// Small notice for Indian customers since US-based Stripe + Indian cards have quirks
function IndiaNotice() {
  // Cheap client-side hint: read browser language as a soft indicator. The actual
  // currency conversion happens at the customer's bank — we just set expectations.
  const lang = (navigator.language || '').toLowerCase();
  const looksIndian = lang.includes('in') || (Intl.DateTimeFormat().resolvedOptions().timeZone || '').includes('Asia/Kolkata');
  if (!looksIndian) return null;
  return (
    <div className="mt-4 bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 text-xs text-blue-100">
      🇮🇳 <strong>Notice for Indian customers:</strong> Prices are in USD. Your bank will convert to INR at their FX rate plus a small foreign transaction fee.
      Recurring subscriptions on Indian cards may require additional bank authorization per charge (RBI guidelines). If you face issues, contact
      <a href="mailto:ashutosh@socialeyes.in" className="underline">ashutosh@socialeyes.in</a>.
    </div>
  );
}

export default CheckoutPage;
